// Präferenzen → Import → „Ordner automatisch überwachen“ (desktop only): new files in the folders become an import
// in Réception (with the local AI when it is on). Files that were already there when watching started are left
// alone; nothing in the folders is moved or deleted. The server skips identical files anyway.
import { invoke } from '@tauri-apps/api/core';
import * as S from '../data/importSessions';
import { tr2 } from '../i18n/lang';

interface Entry { path: string; name: string; size: number; modified: number }
export interface Watched { dir: string; label: string }

const KEY = 'rimon.watch.seen';
const load = (): Record<string, string[]> => { try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; } };
const save = (v: Record<string, string[]>) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* private mode */ } };
const sig = (e: Entry) => `${e.name}|${e.size}|${e.modified}`;
const MIME: Record<string, string> = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif', tif: 'image/tiff', tiff: 'image/tiff', zip: 'application/zip', xml: 'application/xml' };

let timer: ReturnType<typeof setInterval> | null = null, running = false, current: Watched[] = [];
/** Folders that could not be read or created: said once, then skipped quietly until the settings change. */
let missing = new Set<string>();

/** Starts (or reconfigures) watching. `onImport` gets the folder label and the number of files sent to Réception. */
export function watchFolders(dirs: Watched[], onImport: (label: string, n: number) => void, onError: (msg: string) => void) {
  // An empty folder setting switches watching off for that folder.
  current = dirs.filter((d) => d.dir.trim());
  missing = new Set();
  if (timer) clearInterval(timer);
  const tick = async () => {
    if (running) return; running = true;
    try {
      const seen = load(), ai = await S.capabilities().then((c) => c.ai, () => false);
      for (const w of current) {
        let list: Entry[];
        if (missing.has(w.dir)) continue;
        try { list = await invoke<Entry[]>('scan_dir', { dir: w.dir }); }
        catch {
          // Missing folder: one calm hint, then skipped (no folder is created behind the user's back, no repeated toasts).
          missing.add(w.dir); onError(tr2(`${w.label}: Ordner ${w.dir} gibt es nicht · wird nicht überwacht (Präferenzen → Import · leer lassen = aus).`, `${w.label} : le dossier ${w.dir} n’existe pas · non surveillé (Préférences → Import · laisser vide = désactivé).`)); continue;
        }
        const known = seen[w.dir];
        // First look at a folder: what is there already is the baseline, not new.
        if (!known) { seen[w.dir] = list.map(sig); save(seen); continue; }
        const fresh = list.filter((e) => !known.includes(sig(e)) && Date.now() / 1000 - e.modified > 5); // finished copying
        if (!fresh.length) continue;
        const files = await Promise.all(fresh.map(async (e) => {
          const buf = await invoke<ArrayBuffer>('read_file', { path: e.path });
          return new File([buf], e.name, { type: MIME[e.name.split('.').pop()!.toLowerCase()] ?? 'application/octet-stream' });
        }));
        const xml = files.filter((f) => /\.xml$/i.test(f.name)), rest = files.filter((f) => !/\.xml$/i.test(f.name));
        // Kept as an open import (draft): it shows up in Réception for anyone to continue.
        if (rest.length) { const s = await S.createSession(ai ? 'ai' : 'manual'); await S.uploadFiles(s.id, rest); await S.keepAsDraft(s.id).catch(() => undefined); }
        if (xml.length) { const { importStatement } = await import('../data/actions'); for (const f of xml) await importStatement(f); }
        seen[w.dir] = [...known, ...fresh.map(sig)].slice(-2000); save(seen);
        onImport(w.label, files.length);
      }
    } catch (e) { onError(String(e instanceof Error ? e.message : e)); }
    finally { running = false; }
  };
  if (current.length) { void tick(); timer = setInterval(tick, 15_000); }
}
export function stopWatching() { if (timer) clearInterval(timer); timer = null; current = []; }
/** Creates the folder if it does not exist yet; returns the absolute path. */
export const ensureDir = (dir: string) => invoke<string>('ensure_dir', { dir });
